import { Component, Input, OnChanges, SimpleChanges, ViewChild, ElementRef } from '@angular/core';
import { IReportMachineStatus, IStatus, IReportByAllStatus } from '../../../interfaces/machine-status';
import Highcharts from 'highcharts';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { SubstateModalComponent } from '../substate-modal/substate-modal.component';

@Component({
  selector: 'app-pie-chart',
  templateUrl: './pie-chart.component.html',
  styleUrls: ['./pie-chart.component.scss'],
})
export class PieChartComponent implements OnChanges {
  @Input()
  public data: IReportMachineStatus;

  @ViewChild('chartTarget', { read: ElementRef, static: false })
  public chartTarget: ElementRef;

  public legend: IStatus[];

  private reportByAllStatus: IReportByAllStatus[];

  constructor(private modal: NgbModal) {}

  /**
   * On component changes
   */
  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.data && changes.data.currentValue) {
      this.drawChart(changes.data.currentValue);
      this.reportByAllStatus = changes.data.currentValue.reportByAllStatus; // data for detail modal
    }
  }

  /**
   * Draw chart
   *
   * @param data data from endpoint
   */
  private drawChart(reportData: IReportMachineStatus): void {
    this.legend = reportData.reportByStatus;
    const categories: IStatus[] = [];
    const data = [];

    // set status
    reportData.reportByStatus.forEach((status) => {
      categories.push(status);
    });

    // set substatus
    categories.forEach((categorie) => {
      const statusGroup = reportData.reportByStatusFamily.filter((status) => status.statusFamilyName === categorie.statusFamilyName);

      data.push({
        y: categorie.percentage,
        color: categorie.statusFamilyColor,
        drilldown: {
          name: categorie.statusFamilyColor,
          categories: statusGroup.map((status) => status),
          data: statusGroup.map((status) => status.percentage),
        },
      });
    });

    const statusData = [];
    const substatusData = [];
    const dataLength = data.length;
    let i;
    let j;
    let drillDataLength;
    let brightness;

    // Build the data arrays
    for (i = 0; i < dataLength; i += 1) {
      // add staus data
      statusData.push({
        name: categories[i].percentage,
        statusName: categories[i].statusFamilyName,
        y: data[i].y,
        color: data[i].color,
        borderWidth: 1,
        states: {
          hover: {
            brightness: 0,
            borderColor: Highcharts.color(data[i].color).brighten(-0.2).get(),
          },
        },
      });

      // add substatus data
      drillDataLength = data[i].drilldown.data.length;
      for (j = 0; j < drillDataLength; j += 1) {
        brightness = 0.2 - j / drillDataLength / 4;
        substatusData.push({
          name: data[i].drilldown.categories[j],
          status: data[i].drilldown.categories[j],
          y: data[i].drilldown.data[j],
          color: Highcharts.color(data[i].color).get(),
          borderWidth: 1,
          states: {
            hover: {
              brightness: 0,
              borderColor: Highcharts.color(data[i].color).brighten(-0.2).get(),
            },
          },
        });
      }
    }

    const config = {
      chart: {
        type: 'pie',
        margin: [0, 0, 0, 0],
        spacingTop: 0,
        spacingBottom: 0,
        spacingLeft: 0,
        spacingRight: 0,
        backgroundColor: '#f8f8f8',
      },
      credits: {
        enabled: false,
      },
      title: {
        text: '',
      },
      plotOptions: {
        pie: {
          shadow: false,
          center: ['50%', '50%'],
          size: '100%',
        },
        series: {
          cursor: 'pointer',
          point: {
            events: {
              click: (evt) => {
                this.openSubstatusModal(evt.point.options.status);
              },
            },
          },
        },
      },
      tooltip: {
        backgroundColor: '#433e3e',
        borderColor: '#433e3e',
        borderRadius: '4',
        style: {
          color: 'white',
        },
        useHTML: true,
        formatter(): string {
          let label = '';
          // the structure of the status and substatus is different
          if (this.point.options.statusName) {
            label = this.point.options.statusName;
          } else {
            label = this.point.options.status.statusName;
          }

          return `
            <div>
              <span style="padding: 7px .4em; background-color:${this.point.options.color};
                            display: inline-block; font-size: 12px; text-align: center;
                            line-height: 1; white-space: nowrap; vertical-align: baseline;
                            border-radius: 1rem; color: #FFFFFF;">
                ${label}
              </span>
              <br>
              <p style="padding: 0 0 0 0; margin: 5px 0 0 0;">
                ${this.point.options.y}%
              </p>
            </div>
          `;
        },
      },
      series: [
        {
          data: statusData,
          size: '60%',
          innerSize: '40%',
          dataLabels: {
            formatter(): string {
              return this.y > 5 ? `${this.point.name} %` : null;
            },
            color: '#ffffff',
            distance: -50,
          },
        },
        {
          data: substatusData,
          size: '80%',
          innerSize: '60%',
          dataLabels: {
            enabled: false,
          },
          id: 'versions',
        },
      ],
    };
    setTimeout(() => Highcharts.chart(this.chartTarget.nativeElement, config as any, (chart) => {}), 0);
  }

  /**
   * Function to open the modal of substates
   *
   * @param value selected substatus
   */
  public openSubstatusModal(value: any): void {
    if (value) {
      const modalData = {
        selectedStatus: value,
        allStatus: this.reportByAllStatus,
      };

      const modalRef = this.modal.open(SubstateModalComponent, {
        centered: true,
        backdrop: 'static',
        windowClass: 'modal-width',
      });

      modalRef.componentInstance.modalData = modalData;
    }
  }
}
